import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react'
import { ApprovalChecklist, FormFieldsEdit } from '../../../../types/form-types'
import { Permissions } from '../../../../types/user-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'
import ReadOnlyLookupField from '../../wells/readonly-lookup-field'
import {
  CheckableField,
  CheckableFields,
  Divider,
  FormField,
  Heading,
  RecordContent,
  TypeaheadOption,
} from '@msaf/core-react'

interface WellReviewFormProps {
  isLoading: boolean
  fields: Partial<FormFieldsEdit>
  validationErrors: ValidationErrors
  isSetupRecord?: boolean
  setValue: SetValue<FormFieldsEdit>
  handleReviewCheckBox: (e: ChangeEvent<HTMLInputElement>) => void
  isReviewerChanged: boolean
  setIsReviewerChanged: Dispatch<SetStateAction<boolean>>
  isEditable: boolean | undefined
  isPublished: boolean
  setCurrentUserIsAssigned: (arg0: boolean) => void
  currentUserIsAssigned: boolean | undefined
  approvalChecklist?: Partial<ApprovalChecklist>
}

export default function EditWellReviewForm({
  isLoading,
  fields,
  setValue,
  validationErrors,
  handleReviewCheckBox,
  isReviewerChanged,
  setIsReviewerChanged,
  isEditable,
  setCurrentUserIsAssigned,
  currentUserIsAssigned,
  isPublished,
  approvalChecklist,
}: WellReviewFormProps) {
  const [initialReviewer, setInitialReviewer] = useState<TypeaheadOption>({ label: '', value: '' })
  useEffect(() => {
    if (initialReviewer.label === 'Assign to me') {
      setCurrentUserIsAssigned(true)
    } else {
      setCurrentUserIsAssigned(false)
    }
  }, [initialReviewer.label, fields.assignedUser, isReviewerChanged, setCurrentUserIsAssigned])

  const handleReviewerChange = ({ value }: TypeaheadOption) => {
    if (fields.assignedUser && `${fields.assignedUser}` !== value) {
      setIsReviewerChanged(true)
      // Trigger the field to update on change and hide / show review checklist
      setValue('assignedUser', value)
    }
  }

  return (
    <RecordContent>
      <Heading level={2}>Reviewer</Heading>
      <FormField
        validationMessages={validationErrors?.assignedUser}
        isSkeleton={isLoading}
        labelId='assignedReviewerlabelId'
        label='Assigned reviewer'
        isRequired
      >
        {isEditable ? (
          <LookupField
            isSkeleton={isLoading}
            isInvalid={!!validationErrors?.assignedUser}
            labelledBy='assignedReviewerlabelId'
            fieldId='councilStaff'
            fieldFilters={{
              councilId: fields.council ?? '',
              permission: Permissions.REVIEW_WELL,
              readOnlyField: false,
            }}
            value={`${fields.assignedUser ?? ''}`}
            setValue={({ value }) => setValue('assignedUser', value)}
            handleChangeCallback={handleReviewerChange}
            setValueOnInitialLoad={setInitialReviewer}
          />
        ) : (
          <ReadOnlyLookupField
            isSkeleton={isLoading}
            fieldId='councilStaff'
            labelledBy='assignedReviewerlabelId'
            fieldFilters={{ councilId: fields.council ?? '', readOnlyField: true }}
            value={`${fields.assignedUser ?? ''}`}
          />
        )}
      </FormField>
      {/* Hide the checkboxes if the reviewer is not current user */}
      {(currentUserIsAssigned || isPublished) && (
        <>
          <Divider />
          <Heading isSkeleton={isLoading} level={2}>
            Approval checklist
          </Heading>
          <FormField
            label='Confirm the following are complete'
            labelId='confirmationCheckboxLabelId'
            isSkeleton={isLoading}
          >
            <CheckableFields ariaLabelledBy='confirmationCheckboxLabelId'>
              <CheckableField
                id='confirmationDetailsId'
                name='details'
                label='Details complete'
                describedBy='confirmationCheckboxLabelId'
                type='checkbox'
                onChange={(e) => handleReviewCheckBox(e)}
                checked={approvalChecklist?.details}
                isSkeleton={isLoading}
                disabled={!isEditable}
              />

              <CheckableField
                id='confirmationConstructionId'
                name='construction'
                label='Construction complete'
                describedBy='confirmationCheckboxLabelId'
                type='checkbox'
                onChange={(e) => handleReviewCheckBox(e)}
                checked={approvalChecklist?.construction}
                isSkeleton={isLoading}
                disabled={!isEditable}
              />
              <CheckableField
                id='confirmationLithologyId'
                name='lithology'
                label='Lithology complete'
                describedBy='confirmationCheckboxLabelId'
                type='checkbox'
                onChange={(e) => handleReviewCheckBox(e)}
                checked={approvalChecklist?.lithology}
                isSkeleton={isLoading}
                disabled={!isEditable}
              />
              <CheckableField
                id='confirmationWellheadSecurityId'
                name='wellheadSecurity'
                label='Wellhead security complete'
                describedBy='confirmationCheckboxLabelId'
                type='checkbox'
                onChange={(e) => handleReviewCheckBox(e)}
                checked={approvalChecklist?.wellheadSecurity}
                isSkeleton={isLoading}
                disabled={!isEditable}
              />
              <CheckableField
                id='confirmationYieldDrawdownTestsId'
                name='yieldDrawdownTest'
                label='Yield drawdown tests complete'
                describedBy='confirmationCheckboxLabelId'
                type='checkbox'
                onChange={(e) => handleReviewCheckBox(e)}
                checked={approvalChecklist?.yieldDrawdownTest}
                isSkeleton={isLoading}
                disabled={!isEditable}
              />
            </CheckableFields>
          </FormField>
        </>
      )}
    </RecordContent>
  )
}
